import { TransactionItemCheckDeposit } from '@/types/transactions';
import { SVSummaryCard } from '../core';
import { SVSummaryCardItem } from '@/types/common';
import { formatDateTimeFromUTC } from '@/utils/date';
import TransactionAmount from './TransactionAmount';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import {
  getCheckDepositErrorReason,
  getTransactionDeliverySpeedFromTransferType,
} from '@/utils/transactions';
import { buildAddress } from '@/utils/address';
import {
  Card,
  Dialog,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

type CheckDepositTransactionDetailsProps = {
  transactionDetails: TransactionItemCheckDeposit;
};

export default function CheckDepositTransactionDetails({
  transactionDetails,
}: CheckDepositTransactionDetailsProps) {
  const [imageUrl, setImageUrl] = useState('');

  const { isStaff } = useAuthUser();
  const {
    startedAt,
    canceledAt,
    settledAt,
    statusLabel,
    fromAccount,
    toAccount,
    id,
    transferType,
    note,
    description,
    instructions,
    checkDeposit,
  } = transactionDetails;
  const { recipientAddress, bankName } = toAccount || {};

  const items: SVSummaryCardItem[] = [
    {
      label: 'Amount',
      customContent: <TransactionAmount transaction={transactionDetails} />,
    },
    { label: 'Status', text: statusLabel },
    { label: 'Created At', text: formatDateTimeFromUTC(startedAt, true) },
    { label: 'Canceled At', text: formatDateTimeFromUTC(canceledAt, true) },
    { label: 'Settled At', text: formatDateTimeFromUTC(settledAt, true) },
    {
      label: 'Error Reason',
      text: isStaff
        ? getCheckDepositErrorReason(transactionDetails.errorReason)
        : '',
    },
    {
      label: 'From Account',
      text: fromAccount?.bankName || '',
    },
    {
      label: 'To Account',
      text: bankName,
    },
    {
      label: 'To Address',
      text: recipientAddress
        ? buildAddress({
            line1: recipientAddress.addressLine1,
            line2: recipientAddress.addressLine2,
            city: recipientAddress.addressCity,
            state: recipientAddress.addressStateCode,
            postalCode: recipientAddress.addressPostalCode,
          })
        : '',
    },
    { label: 'Transaction Id', text: id },
    {
      label: 'Payment Type',
      text: getTransactionDeliverySpeedFromTransferType(transferType),
    },
    { label: 'Note', text: note || '' },
    { label: 'Transaction Description', text: description },
    { label: 'Instructions', text: instructions || '' },
  ];

  return (
    <Stack spacing={2}>
      <SVSummaryCard label="Basic Info" items={items} />
      <Card>
        <Typography variant="h5">Check Images</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography fontWeight="bold">Back</Typography>
            <IconButton onClick={() => setImageUrl(checkDeposit.imageBackUrl)}>
              <img
                style={{ maxWidth: '100%' }}
                src={checkDeposit.imageBackUrl}
              />
            </IconButton>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography fontWeight="bold">Front</Typography>
            <IconButton onClick={() => setImageUrl(checkDeposit.imageFrontUrl)}>
              <img
                style={{ maxWidth: '100%' }}
                src={checkDeposit.imageFrontUrl}
              />
            </IconButton>
          </Grid>
        </Grid>
        <Dialog
          fullWidth
          maxWidth="xl"
          open={Boolean(imageUrl)}
          onClose={() => setImageUrl('')}>
          <img src={imageUrl} style={{ maxWidth: '100%' }} />
        </Dialog>
      </Card>
    </Stack>
  );
}
